import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid-pro';

import { translate as t } from 'hooks/useTranslations';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import RemoveButton from './RemoveButton';

import type { RelatedOrganization } from '../../_types';

interface OrganizationTableProps {
  organizations: RelatedOrganization[];
  filteredOrganizations: RelatedOrganization[];
  organizationsQuery: {
    isError: boolean;
    isLoading: boolean;
    error: any;
  };
  stateMap: Record<string, string>;
  hasPermission: (permission: string, orgId: string) => boolean;
  currentOrganizationId: string | null;
}

const OrganizationTable: React.FC<OrganizationTableProps> = ({
  organizations,
  filteredOrganizations,
  organizationsQuery,
  stateMap,
  hasPermission,
  currentOrganizationId,
}) => {
  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('riskPoolSharing.properties.organizationName'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Box component="span" sx={{ fontWeight: 600 }}>
              {row.name}
            </Box>
          );
        },
      },
      state: {
        header: t('riskPoolSharing.properties.jurisdiction'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const stateAbbr = row.state;
          const stateName = stateAbbr ? stateMap[stateAbbr] || stateAbbr : '-';
          return <Box component="span">{stateName}</Box>;
        },
      },
      city: {
        header: t('riskPoolSharing.properties.city'),
        flex: 1,
      },
      lastUpdate: {
        header: t('riskPoolSharing.properties.updated'),
        flex: 0.8,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Box component="span">
              {new Date(row.lastUpdate).toLocaleDateString()}
            </Box>
          );
        },
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.3,
        renderer: (params: GridCellParams) => {
          return <RemoveButton params={params} organizations={organizations} />;
        },
      },
    }),
    [t, stateMap, organizations, hasPermission, currentOrganizationId]
  );

  const gridViewQuery = useMemo(
    () => ({
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: () => Promise.resolve(),
      isError: organizationsQuery.isError,
      isLoading: organizationsQuery.isLoading,
      error: organizationsQuery.error,
      data: {
        pages: [
          {
            count: filteredOrganizations.length,
            rows: filteredOrganizations,
          },
        ],
      },
    }),
    [filteredOrganizations, organizationsQuery.isLoading, organizationsQuery.isError, organizationsQuery.error]
  );

  return (
    <GridSection
      columns={columns}
      data={filteredOrganizations}
      query={gridViewQuery}
    />
  );
};

export default OrganizationTable;

